/**
 * Position Reconcile Modal Component
 *
 * Shown when user manually updates position quantity and it doesn't match
 * the sum of all trades. Prompts user to add a reconciling trade.
 * NOW THEME-AWARE
 */

import React, { useState, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Modal,
  ScrollView,
  TextInput,
  ActivityIndicator,
} from 'react-native';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { DatePicker } from '../ui/DatePicker';

export type TransactionType = 'buy' | 'sell' | 'deposit' | 'withdrawal';

export interface ReconcileData {
  position_id: number;
  symbol: string;
  currency?: string;
  current_quantity: number;
  target_quantity: number;
  difference: number;
  suggested_trade: {
    transaction_type: TransactionType;
    quantity: number;
    suggested_price: number;
  };
}

interface PositionReconcileModalProps {
  visible: boolean;
  reconcileData: ReconcileData | null;
  onConfirm: (tradeData: {
    transaction_type: TransactionType;
    quantity: number;
    trade_price: number;
    trade_datetime: string;
    comment?: string;
  }) => void;
  onCancel: () => void;
  loading?: boolean;
}

export function PositionReconcileModal({
  visible,
  reconcileData,
  onConfirm,
  onCancel,
  loading = false,
}: PositionReconcileModalProps) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  const [selectedTransactionType, setSelectedTransactionType] = useState<TransactionType>('buy');
  const [tradePrice, setTradePrice] = useState('');
  const [comment, setComment] = useState('');
  const [tradeDate, setTradeDate] = useState<string>(getCurrentDate());

  // Debug logging
  React.useEffect(() => {
    console.log('[PositionReconcileModal] Props changed:', { visible, reconcileData, loading });
  }, [visible, reconcileData, loading]);

  // Update state when reconcileData changes
  React.useEffect(() => {
    console.log('[PositionReconcileModal] reconcileData changed:', reconcileData);
    if (reconcileData) {
      setSelectedTransactionType(reconcileData.suggested_trade.transaction_type);
      setTradePrice(reconcileData.suggested_trade.suggested_price.toString());
      setComment('');
      setTradeDate(getCurrentDate());
    }
  }, [reconcileData]);

  // Helper to get current date in YYYY-MM-DD format
  function getCurrentDate(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  if (!reconcileData) {
    return null;
  }

  const isPositiveDifference = reconcileData.difference > 0;
  const absDifference = Math.abs(reconcileData.difference);

  const handleConfirm = () => {
    const price = parseFloat(tradePrice) || reconcileData.suggested_trade.suggested_price;

    // Convert date string to ISO datetime string
    const trade_datetime = tradeDate ? new Date(tradeDate).toISOString() : new Date().toISOString();

    onConfirm({
      transaction_type: selectedTransactionType,
      quantity: absDifference,
      trade_price: price,
      trade_datetime,
      comment: comment.trim() || undefined,
    });
  };

  const transactionTypes: { value: TransactionType; label: string; description: string }[] = [
    {
      value: 'buy',
      label: t('portfolio.buy'),
      description: t('portfolio.buyDescription', { defaultValue: 'Purchase shares' }),
    },
    {
      value: 'deposit',
      label: t('portfolio.deposit'),
      description: t('portfolio.depositDescription', {
        defaultValue: 'Deposit shares into portfolio',
      }),
    },
    {
      value: 'sell',
      label: t('portfolio.sell'),
      description: t('portfolio.sellDescription', { defaultValue: 'Sell shares' }),
    },
    {
      value: 'withdrawal',
      label: t('portfolio.withdrawal'),
      description: t('portfolio.withdrawalDescription', {
        defaultValue: 'Withdraw shares from portfolio',
      }),
    },
  ];

  // Filter transaction types based on difference
  const availableTransactionTypes = isPositiveDifference
    ? transactionTypes.filter((t) => t.value === 'buy' || t.value === 'deposit')
    : transactionTypes.filter((t) => t.value === 'sell' || t.value === 'withdrawal');

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onCancel}>
      <View style={styles.overlay}>
        <View style={[styles.modalContainer, { backgroundColor: theme.colors.surface }]}>
          {/* Header */}
          <View style={[styles.header, { borderBottomColor: theme.colors.border }]}>
            <Text style={[styles.title, { color: theme.colors.text }]}>
              {t('portfolio.positionMismatch', { defaultValue: 'Position Quantity Mismatch' })}
            </Text>
            <TouchableOpacity
              onPress={onCancel}
              style={styles.closeButton}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <Text style={[styles.closeButtonText, { color: theme.colors.textSecondary }]}>✕</Text>
            </TouchableOpacity>
          </View>

          {/* Content */}
          <ScrollView style={styles.content}>
            {/* Quantity Comparison */}
            <View
              style={[
                styles.comparisonBox,
                {
                  backgroundColor: theme.colors.backgroundElevated,
                  borderColor: theme.colors.border,
                },
              ]}
            >
              <View style={styles.comparisonRow}>
                <Text style={[styles.comparisonLabel, { color: theme.colors.textSecondary }]}>
                  {t('portfolio.currentQuantity', {
                    defaultValue: 'Current Quantity (from trades)',
                  })}
                  :
                </Text>
                <Text style={[styles.comparisonValue, { color: theme.colors.text }]}>
                  {reconcileData.current_quantity.toFixed(2)}
                </Text>
              </View>

              <View style={styles.comparisonRow}>
                <Text style={[styles.comparisonLabel, { color: theme.colors.textSecondary }]}>
                  {t('portfolio.targetQuantity', { defaultValue: 'Your Target Quantity' })}:
                </Text>
                <Text style={[styles.comparisonValue, { color: theme.colors.text }]}>
                  {reconcileData.target_quantity.toFixed(2)}
                </Text>
              </View>

              <View style={[styles.comparisonDivider, { backgroundColor: theme.colors.border }]} />

              <View style={styles.comparisonRow}>
                <Text style={[styles.comparisonLabel, { color: theme.colors.textSecondary }]}>
                  {t('portfolio.difference', { defaultValue: 'Difference' })}:
                </Text>
                <Text
                  style={[
                    styles.comparisonValue,
                    styles.comparisonValueHighlight,
                    { color: isPositiveDifference ? theme.colors.success : theme.colors.error },
                  ]}
                >
                  {isPositiveDifference ? '+' : ''}
                  {reconcileData.difference.toFixed(2)}
                </Text>
              </View>
            </View>

            {/* Explanation */}
            <View
              style={[
                styles.infoBox,
                { backgroundColor: theme.colors.primary + '10', borderColor: theme.colors.primary },
              ]}
            >
              <Text style={[styles.infoIcon, { color: theme.colors.primary }]}>ℹ️</Text>
              <Text style={[styles.infoText, { color: theme.colors.text }]}>
                {isPositiveDifference
                  ? t('portfolio.reconcileAddMessage', {
                      defaultValue: `Your position has fewer shares than your trades indicate. Please add a trade to reconcile the difference of ${absDifference.toFixed(2)} shares.`,
                      diff: absDifference.toFixed(2),
                    })
                  : t('portfolio.reconcileRemoveMessage', {
                      defaultValue: `Your position has more shares than your trades indicate. Please add a trade to reconcile the difference of ${absDifference.toFixed(2)} shares.`,
                      diff: absDifference.toFixed(2),
                    })}
              </Text>
            </View>

            {/* Trade Date Picker */}
            <View style={styles.fieldContainer}>
              <Text style={[styles.label, { color: theme.colors.text }]}>
                {t('portfolio.tradeDate', { defaultValue: 'Trade Date' })} *
              </Text>
              <DatePicker
                value={tradeDate}
                onChange={setTradeDate}
                placeholder={t('portfolio.selectDate', { defaultValue: 'Select date' })}
              />
            </View>

            {/* Transaction Type Selector */}
            <View style={styles.fieldContainer}>
              <Text style={[styles.label, { color: theme.colors.text }]}>
                {t('portfolio.transactionType', { defaultValue: 'Transaction Type' })} *
              </Text>
              <View style={styles.transactionTypesContainer}>
                {availableTransactionTypes.map((type) => (
                  <TouchableOpacity
                    key={type.value}
                    style={[
                      styles.transactionTypeButton,
                      {
                        backgroundColor:
                          selectedTransactionType === type.value
                            ? theme.colors.primary
                            : theme.colors.backgroundElevated,
                        borderColor:
                          selectedTransactionType === type.value
                            ? theme.colors.primary
                            : theme.colors.border,
                        borderWidth: theme.id === 'bauhaus' ? 2 : 1,
                      },
                    ]}
                    onPress={() => setSelectedTransactionType(type.value)}
                    disabled={loading}
                    activeOpacity={0.7}
                  >
                    <Text
                      style={[
                        styles.transactionTypeButtonText,
                        {
                          color:
                            selectedTransactionType === type.value
                              ? theme.colors.textInverse
                              : theme.colors.text,
                        },
                      ]}
                    >
                      {type.label}
                    </Text>
                    <Text
                      style={[
                        styles.transactionTypeDescription,
                        {
                          color:
                            selectedTransactionType === type.value
                              ? theme.colors.textInverse + '99'
                              : theme.colors.textTertiary,
                        },
                      ]}
                    >
                      {type.description}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            {/* Price Input */}
            <View style={styles.fieldContainer}>
              <Text style={[styles.label, { color: theme.colors.text }]}>
                {t('portfolio.tradePrice', { defaultValue: 'Trade Price' })} *
              </Text>
              <TextInput
                style={[
                  styles.input,
                  {
                    backgroundColor: theme.colors.backgroundElevated,
                    borderColor: theme.colors.border,
                    color: theme.colors.text,
                  },
                ]}
                value={tradePrice}
                onChangeText={setTradePrice}
                placeholder={`${reconcileData.suggested_trade.suggested_price.toFixed(2)}`}
                placeholderTextColor={theme.colors.textTertiary}
                keyboardType="decimal-pad"
                editable={!loading}
              />
              <Text style={[styles.helperText, { color: theme.colors.textTertiary }]}>
                {t('portfolio.currentPriceSuggestion', {
                  defaultValue: 'Suggested: current price',
                })}
              </Text>
            </View>

            {/* Comment Input (Optional) */}
            <View style={styles.fieldContainer}>
              <Text style={[styles.label, { color: theme.colors.text }]}>
                {t('portfolio.comment', { defaultValue: 'Comment' })} (
                {t('common.optional', { defaultValue: 'Optional' })})
              </Text>
              <TextInput
                style={[
                  styles.textArea,
                  {
                    backgroundColor: theme.colors.backgroundElevated,
                    borderColor: theme.colors.border,
                    color: theme.colors.text,
                  },
                ]}
                value={comment}
                onChangeText={setComment}
                placeholder={t('portfolio.reconcileCommentPlaceholder', {
                  defaultValue: 'Add a note about this reconciling trade...',
                })}
                placeholderTextColor={theme.colors.textTertiary}
                multiline
                numberOfLines={2}
                maxLength={2000}
                editable={!loading}
                textAlignVertical="top"
              />
            </View>

            {/* Summary */}
            <View
              style={[
                styles.summaryBox,
                {
                  backgroundColor: theme.colors.backgroundElevated,
                  borderColor: theme.colors.border,
                },
              ]}
            >
              <Text style={[styles.summaryTitle, { color: theme.colors.text }]}>
                {t('portfolio.summary', { defaultValue: 'Summary' })}:
              </Text>
              <Text style={[styles.summaryText, { color: theme.colors.textSecondary }]}>
                {t('portfolio.reconcileSummary', {
                  defaultValue:
                    'This will create a new trade to reconcile the position quantity with the sum of all trades.',
                })}
              </Text>
            </View>
          </ScrollView>

          {/* Footer Buttons */}
          <View style={[styles.footer, { borderTopColor: theme.colors.border }]}>
            <TouchableOpacity
              style={[
                styles.footerButton,
                styles.cancelButton,
                {
                  backgroundColor: theme.colors.backgroundElevated,
                  borderColor: theme.colors.border,
                },
              ]}
              onPress={onCancel}
              disabled={loading}
            >
              <Text style={[styles.footerButtonText, { color: theme.colors.textSecondary }]}>
                {t('common.cancel')}
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.footerButton,
                styles.confirmButton,
                { backgroundColor: theme.colors.primary },
              ]}
              onPress={handleConfirm}
              disabled={loading}
            >
              {loading ? (
                <ActivityIndicator size="small" color={theme.colors.textInverse} />
              ) : (
                <Text style={[styles.footerButtonText, { color: theme.colors.textInverse }]}>
                  {t('portfolio.addTradeAndReconcile', { defaultValue: 'Add Trade & Save' })}
                </Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

/**
 * Create themed styles dynamically
 */
function createThemedStyles(theme: any) {
  return StyleSheet.create({
    overlay: {
      flex: 1,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      justifyContent: 'center',
      alignItems: 'center',
      padding: 20,
    },
    modalContainer: {
      width: '100%',
      maxWidth: 500,
      maxHeight: '80%',
      borderRadius: theme.radii.lg,
      borderWidth: theme.id === 'bauhaus' ? 2 : 0,
      borderColor: theme.colors.border,
    },
    header: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: 16,
      borderBottomWidth: theme.id === 'bauhaus' ? 2 : 1,
    },
    title: {
      fontSize: theme.typography.fontSizeLg,
      fontWeight: theme.typography.fontWeightBold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
    closeButton: {
      padding: 4,
    },
    closeButtonText: {
      fontSize: 20,
      fontWeight: 'bold',
    },
    content: {
      padding: 16,
      maxHeight: 400,
    },
    comparisonBox: {
      padding: 16,
      borderRadius: theme.radii.md,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      marginBottom: 16,
    },
    comparisonRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 8,
    },
    comparisonLabel: {
      fontSize: theme.typography.fontSizeSm,
      flex: 1,
    },
    comparisonValue: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    comparisonValueHighlight: {
      fontSize: theme.typography.fontSizeLg,
    },
    comparisonDivider: {
      height: 1,
      marginVertical: 4,
    },
    infoBox: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      padding: 12,
      borderRadius: theme.radii.md,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      marginBottom: 16,
    },
    infoIcon: {
      fontSize: 16,
      marginRight: 8,
    },
    infoText: {
      flex: 1,
      fontSize: theme.typography.fontSizeSm,
    },
    fieldContainer: {
      marginBottom: 16,
    },
    label: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
      marginBottom: 8,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
      letterSpacing: theme.id === 'bauhaus' ? 1 : 0,
    },
    input: {
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderRadius: theme.radii.md,
      padding: 12,
      fontSize: theme.typography.fontSizeMd,
    },
    textArea: {
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderRadius: theme.radii.md,
      padding: 12,
      fontSize: theme.typography.fontSizeMd,
      minHeight: 60,
    },
    helperText: {
      fontSize: theme.typography.fontSizeXs,
      marginTop: 4,
    },
    transactionTypesContainer: {
      gap: 8,
    },
    transactionTypeButton: {
      paddingVertical: 12,
      paddingHorizontal: 16,
      borderRadius: theme.radii.md,
      alignItems: 'flex-start',
    },
    transactionTypeButtonText: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
      marginBottom: 4,
    },
    transactionTypeDescription: {
      fontSize: theme.typography.fontSizeXs,
    },
    summaryBox: {
      padding: 12,
      borderRadius: theme.radii.md,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
    },
    summaryTitle: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
      marginBottom: 8,
    },
    summaryText: {
      fontSize: theme.typography.fontSizeXs,
    },
    footer: {
      flexDirection: 'row',
      gap: 12,
      padding: 16,
      borderTopWidth: theme.id === 'bauhaus' ? 2 : 1,
    },
    footerButton: {
      flex: 1,
      paddingVertical: 14,
      paddingHorizontal: 24,
      borderRadius: theme.radii.md,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 44,
      borderWidth: theme.id === 'bauhaus' ? 2 : 0,
    },
    cancelButton: {},
    confirmButton: {},
    footerButtonText: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
  });
}
